import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-advanced-image-viewer',
  standalone: true,
  imports: [
    CommonModule, MatDialogModule, MatButtonModule, MatIconModule, 
    MatSliderModule, FormsModule, MatTooltipModule, DragDropModule
  ],
  templateUrl: './advanced-image-viewer.component.html',
  styleUrls: ['./advanced-image-viewer.component.scss']
})
export class AdvancedImageViewerComponent {
  scale = 1;
  rotation = 0;
  brightness = 100;
  contrast = 100;
  flipX = 1; // 1 = Normal, -1 = Efecto Espejo

  constructor(
    public dialogRef: MatDialogRef<AdvancedImageViewerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { url: string }
  ) {}

  zoomIn() { this.scale = Math.min(this.scale + 0.25, 5); } 
  zoomOut() { this.scale = Math.max(this.scale - 0.25, 0.5); } 
  rotateLeft() { this.rotation -= 90; }
  rotateRight() { this.rotation += 90; }
  toggleFlip() { this.flipX = this.flipX === 1 ? -1 : 1; }

  // Descarga usando HTML5 Canvas
    async downloadImage() {
    try {
      const img = new Image();
      
      // 1. Cabecera de seguridad obligatoria para Canvas
      img.crossOrigin = 'anonymous'; 
      
      // (Cache-Buster): Si el navegador ya tenía la foto en caché sin CORS,
      // la reutilizaba y rompía el Canvas. Agregar un timestamp ("?t=123") fuerza al navegador
      // a pedir una copia limpia y autorizada al servidor, solucionando el bloqueo.
      const unificador = this.data.url.includes('?') ? '&' : '?';
      img.src = this.data.url + unificador + 't=' + new Date().getTime();
      
      // Promesa para asegurar la carga completa de la imagen procesada
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error('Bloqueo de CORS o recurso no alcanzable.'));
      });

      // Crear el lienzo (Canvas) oculto
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No se pudo obtener el contexto del Canvas');

      // Calcular dimensiones dinámicas de rotación (90° / 270°)
      const esOrtogonal = Math.abs(this.rotation % 180) === 90;
      const canvasWidth = esOrtogonal ? img.naturalHeight : img.naturalWidth;
      const canvasHeight = esOrtogonal ? img.naturalWidth : img.naturalHeight;

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Aplicar filtros de Brillo y Contraste
      ctx.filter = `brightness(${this.brightness}%) contrast(${this.contrast}%)`;

      // Mover el eje central al medio para rotaciones y efecto espejo estables
      ctx.translate(canvasWidth / 2, canvasHeight / 2);
      ctx.rotate((this.rotation * Math.PI) / 180);
      ctx.scale(this.flipX, 1);

      // Dibujar la imagen perfeccionada en el lienzo
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

      // Exportar el lienzo como archivo descargable
      canvas.toBlob((blob) => {
        if (!blob) throw new Error('Error al empaquetar el lienzo');
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Evidencia_Perfeccionada_${new Date().getTime()}.jpeg`;
        
        document.body.appendChild(link);
        link.click();
        
        // Limpieza de memoria instantánea
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 'image/jpeg', 0.95); // Alta calidad de compresión (95%)

    } catch (error) {
      console.error('Error en renderizado forense de imagen:', error);
      // Si el Canvas definitivo sigue fallando por CORS del servidor, abre el crudo original
      window.open(this.data.url, '_blank');
    }
  }

  reset() {
    this.scale = 1;
    this.rotation = 0;
    this.brightness = 100;
    this.contrast = 100;
    this.flipX = 1; 
  }

  getTransform(): string {
    return `scale(${this.scale}) scaleX(${this.flipX}) rotate(${this.rotation}deg)`;
  }

  getFilter(): string {
    return `brightness(${this.brightness}%) contrast(${this.contrast}%)`;
  }
}